import axios from 'axios'
import * as Sentry from '@sentry/vue'
import { useAuthStore } from '@/modules/auth/stores/useAuthStore'
import { logger } from '@/core/utils/logger'
// Type augmentation for custom axios config properties
import '@/core/api/axiosTypes'

/**
 * Get dynamic API URL based on current hostname
 *
 * Multi-tenant same-site approach:
 * - admin.example.local → api.example.local
 * - partner.example.com → api.example.com
 * - demo-admin.example.com → demo-api.example.com
 *
 * This enables same-site cookies (SameSite=Lax) which are more secure
 * and will continue working when browsers block third-party cookies.
 */
function getApiUrl(): string {
  // Allow explicit override via environment variable (for local dev, CI, Playwright, Storybook)
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }

  const hostname = window.location.hostname
  const parts = hostname.split('.')

  if (parts.length < 2) {
    throw new Error(`Invalid hostname format: ${hostname}`)
  }

  const subdomain = parts[0]
  const parentDomain = parts.slice(1).join('.')

  // Handle prefixed environments (e.g., demo-admin → demo-api, staging-admin → staging-api)
  const prefixMatch = subdomain.match(/^(.+)-(admin|partner|coin)$/)
  if (prefixMatch) {
    const prefix = prefixMatch[1]
    return `https://${prefix}-api.${parentDomain}`
  }

  // Default: admin.example.com → api.example.com
  return `https://api.${parentDomain}`
}

const api = axios.create({
  baseURL: getApiUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
  withCredentials: true, // Send HTTP-only cookies (refresh token) with requests
})

// Access token storage (in memory - not localStorage for security)
// This is the industry standard for cross-domain SPA authentication
let accessToken: string | null = null

// Export function to set access token (called by auth store after login/refresh)
export function setAccessToken(token: string | null) {
  accessToken = token
}

// Export function to get access token (for debugging/testing)
export function getAccessToken(): string | null {
  return accessToken
}

// Token refresh state - mutex pattern to prevent race conditions
let refreshPromise: Promise<void> | null = null
let isLoggingOut = false

/**
 * Refresh tokens using mutex pattern.
 * All concurrent 401 requests will wait on the same promise.
 */
async function refreshWithMutex(auth: ReturnType<typeof useAuthStore>): Promise<void> {
  if (!refreshPromise) {
    logger.info('[Axios] Starting token refresh...')
    refreshPromise = auth.refreshTokens()
      .then(() => {
        logger.info('[Axios] Token refresh successful')
      })
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

// Export function to set logout state (called by auth store)
export function setLoggingOut(value: boolean) {
  isLoggingOut = value
}

// Request interceptor - add Authorization header
api.interceptors.request.use(
  (config) => {
    // Add access token to Authorization header (cross-domain authentication)
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`
    }
    return config
  },
  (error) => {
    const errorInstance = error instanceof Error ? error : new Error(String(error))
    throw errorInstance
  }
)

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const auth = useAuthStore()
    const originalRequest = error.config

    // Handle network errors (API not available)
    if (!error.response) {
      logger.error('Network error - API not available')
      const errorInstance = error instanceof Error ? error : new Error(String(error))
      throw errorInstance
    }

    // Handle 401 authentication errors by attempting token refresh
    // This handles: missing token (after page refresh), expired token, invalid token
    // Skip if request explicitly disabled auth refresh (e.g., login, refresh-token endpoints)
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.skipAuthRefresh) {
      // If we're already logging out, reject immediately
      if (isLoggingOut) {
        throw new Error('Logging out')
      }

      originalRequest._retry = true

      try {
        // Mutex pattern: all concurrent 401s wait on the same refresh promise
        await refreshWithMutex(auth)
        // Retry original request with new access token
        return api(originalRequest)
      } catch (refreshError: unknown) {
        // Refresh failed - session is invalid, logout
        logger.warn('[Axios] Token refresh failed, logging out')
        isLoggingOut = true
        const errorInstance = refreshError instanceof Error ? refreshError : new Error(String(refreshError))
        // Await logout to ensure proper cleanup before rejecting
        await auth.logout()
        throw errorInstance
      }
    }

    // Log API errors to Sentry in production
    if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
      Sentry.captureException(error, {
        tags: {
          type: 'api_error',
          status: error.response?.status
        },
        extra: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL
        }
      })
    }

    const errorInstance = error instanceof Error ? error : new Error(String(error))
    throw errorInstance
  }
)

export default api
